import { Module } from 'vuex';
import Question from '../models/Question'
import OriginalQuestion from '../../api/v2/models/question'
import { EditorState, EDITOR_MODE } from './types'
import { RootState, CursoPosition } from '../types'
import Skill from '../models/Skill'
import SkillItem from '../models/SkillItem'
import ExamRevision from '../../api/v2/models/exam_revision';
import { ISkillEnpoints, ISkillItemEndpoints, ISchool, IExam } from '../../../types'
import ErrorRange from '../lib/ErrorRange'
import { Range as AceRange } from 'brace'
import EdittorQuestion from '../models/Question'
import humps from 'humps'
import Comparison from '../lib/comparison';
import questionJoiner from '../../../helpers/questions-joiner';
import Vue from 'vue';
import SkillAPI from '../api/SkillAPI';
import SkillItemAPI from '../api/SkillItemAPI';
import api from '../../api/v2/api';
import { ApiError } from '../../api/v2/api/base/response';
import ExamRevisionForm from '../../api/v2/forms/exam_revision';
import QuestionEditorAPI from '../api/QuestionEditorAPI';
import { makeChannel } from '../../../action_cable/cable';
import { asyncSleep } from '../../client/utils/utils';

export type ExamGenerationChanelCallbackType = (content: string, chunk: string, finished: boolean) => void

export const store: Module<EditorState, RootState> = {
  namespaced: true,
}
let setQuestionTimeout
class QEditorState {
  school: ISchool = { endpoints: {}, use_ai_exam_generation: false }
  exam: IExam = { endpoints: {} }
  qs: OriginalQuestion[] = []
  body: string = ""
  row: number = 0
  column: number = 0
  range: AceRange | null = null
  currentPage: number = 0
  questions: EdittorQuestion[] = []
  loaded: boolean = false
  skills: Skill[] = []
  saveMessage: string = ""
  hasError: boolean = false
  errorLines: ErrorRange[] = []
  currentLine: string = ""
  previousLine: string = ""
  showSidebarMenu: string = "metadata"
  breadcrumbs: string[][] = []
  comparison: Comparison | null = null
  editorMode: EDITOR_MODE = EDITOR_MODE.EDITOR
  examRevisions: ExamRevision[] = []
  currentExamRevision: ExamRevision | null = null
  latestBody: string = ""
  isLoading: boolean = false
  examGenerationChanel: ActionCable.Subscription | null = null
  examGenerationChanelFollowed = false
  private examGenerationChanelCallback: ExamGenerationChanelCallbackType = ()=>{}

  commitLoadData(payload: {
      breadcrumbs: string[][],
      school: ISchool,
      exam: IExam,
      questionBody: string,
      questions: { [key: string]: any }[],
      skills: {
        id: number,
        name: string,
        criteria: string,
        endpoints: ISkillEnpoints,
        skill_items: {
          id: number,
          endpoints: ISkillItemEndpoints,
          name: string
        }[]
      }[]
    }) {
    this.breadcrumbs = payload.breadcrumbs
    this.school = payload.school
    this.exam = payload.exam
    this.body = payload.questionBody
    this.currentPage = 0;
    this.loaded = true;
    this.skills = payload.skills.map((skillData) => {
      const skill = new Skill(skillData.endpoints, skillData.id, skillData.name, []);
      skillData.skill_items.forEach(skillItemData => {
        const skillItem = new SkillItem(skillItemData.endpoints, skillItemData.id, skillItemData.name, skill);
        skill.skillItems.push(skillItem);
      });
      return skill;
    })

    if (payload.questions) {
      this.qs = humps.camelizeKeys(payload.questions).map(element => {
        return new OriginalQuestion(element)
      }
      );
    }
  }

  commitResetData(
    payload: {
      school: ISchool,
      exam: IExam,
      question: string,
      questions: { [key: string]: any }[],
      skills: {
        id: number,
        name: string,
        criteria: string,
        endpoints: ISkillEnpoints,
        skill_items: {
          id: number,
          endpoints: ISkillItemEndpoints,
          name: string
        }[]
      }[]
    }) {
    this.school = payload.school
    this.exam = payload.exam
    // https://github.com/happa/skillhub/issues/1479
    // 改行の状態を守るため、Bodyをリセットしないようにする
    // this.body = payload.question
    this.skills = payload.skills.map((skillData) => {
      const skill = new Skill(skillData.endpoints, skillData.id, skillData.name, []);
      skillData.skill_items.forEach(skillItemData => {
        const skillItem = new SkillItem(skillItemData.endpoints, skillItemData.id, skillItemData.name, skill);
        skill.skillItems.push(skillItem);
      });
      return skill;
    })

    if (payload.questions) {
      this.qs = humps.camelizeKeys(payload.questions).map(element =>
        new OriginalQuestion(element)
      );
    }
  }

  commitEditCursoPosition(payload: { row: number, column: number }) {
    this.row = payload.row;
    this.column = payload.column;

    let currentQuestion = 0;
    let sum_row = -1;
    for (let questionIndex = 0; questionIndex < this.questions.length; questionIndex++) {
      currentQuestion = questionIndex;
      sum_row += this.questions[questionIndex].length + 1;
      if (sum_row >= payload.row) {
        break;
      }
    }
    this.currentPage = currentQuestion;
  }

  commitEditRange(payload: { range: AceRange }) {
    this.range = payload.range;
  }

  commitInsert(payload: { content: string, row: number, column: number }) {
    let row = payload.row == null ? this.row : payload.row;
    let column = payload.column == null ? this.column : payload.column;
    let lines: string[] = this.body.split("\n");
    const currentLine = lines[row];
    lines[row] = currentLine.substr(0, column) + payload.content + currentLine.substr(column);
    this.body = lines.join("\n");
  }

  commitWrap(payload: { open: string, end: string }) {
    let lines: string[] = this.body.split("\n");
    lines[this.range.end.row] = `${lines[this.range.end.row].substr(0, this.range.end.column)}${payload.end}\
${lines[this.range.end.row].substr(this.range.end.column)}`;
    lines[this.range.start.row] = `${lines[this.range.start.row].substr(0, this.range.start.column)}${payload.open}\
${lines[this.range.start.row].substr(this.range.start.column)}`;
    this.body = lines.join("\n");
  }

  commitUpdateQuestions() {
    let newQuestions: Question[] = []
    let index = 0
    const oldQuestions = this.questions
    this.body.split(/\n===+\n/).forEach((grouQuestionBody, groupIndex) => {
      newQuestions = newQuestions.concat(grouQuestionBody.split(/\n---+\n/).map((questionBody) => {
        let oldQuestion = oldQuestions[index]
        index += 1;
        if (oldQuestion && oldQuestion.rawBody == questionBody) return oldQuestion
        return new Question(index, questionBody, groupIndex)
      }))
    })
    if (newQuestions.length == 0) newQuestions = [new Question(1)]
    if (this.currentPage >= newQuestions.length) this.currentPage = newQuestions.length - 1
    this.questions = newQuestions
  }

  addSelectedSkillItem(payload: { skillName: string, criteria: string, name: string }) {
    this.questions[this.currentPage].addSkillItem(payload.skillName, payload.criteria, payload.name)
    this.body = questionJoiner(this.questions)
  }

  removeSelectedSkillItem(payload: { skillName: string, name: string }) {
    this.questions[this.currentPage].removeSkillItem(payload.skillName, payload.name)
    this.body = questionJoiner(this.questions)
  }

  commitUpdateExplanation(payload: { explanation: string }) {
    this.questions[this.currentPage].description = payload.explanation
    this.questions[this.currentPage].updateBody()
    this.body = questionJoiner(this.questions)
  }

  commitUpdateSkill(payload: { name: string, id: number, endpoints: ISkillEnpoints }) {
    let needReloadBody = false
    const skill = this.skills.find((skill) => skill.id == payload.id)
    const skill_index = this.skills.indexOf(skill)
    this.questions.forEach((question) => {
      const questionSkill = question.skills[skill.name]
      if (!questionSkill) return

      Vue.delete(question.skills, skill.name)
      Vue.set(question.skills, payload.name, questionSkill)

      question.updateBody()
      needReloadBody = true
    })
    skill.name = payload.name
    if (needReloadBody) this.body = questionJoiner(this.questions)
  }

  commitRemoveSkill(payload: { id: number }) {
    let needReloadBody = false
    const skill = this.skills.find((skill) => skill.id == payload.id)
    const skill_index = this.skills.indexOf(skill)
    this.questions.forEach((question) => {
      const questionSkill = question.skills[skill.name]
      if (!questionSkill) return

      Vue.delete(question.skills, skill.name)
      question.updateBody()
      needReloadBody = true
    })
    this.skills.splice(skill_index, 1)
    if (needReloadBody) this.body = questionJoiner(this.questions)
  }

  commitAddSkillItem(payload: { id: number, name: string, skill_id: number, endpoints: ISkillItemEndpoints }) {
    const skill = this.skills.find((skill) => skill.id == payload.skill_id)
    skill.skillItems.push(new SkillItem(payload.endpoints, payload.id, payload.name, skill))
  }

  commitUpdateSkillItem(payload: { name: string, criteria: string, skill_id: number, id: number }) {
    let needReloadBody = false
    let skill = this.skills.find((skill) => skill.id == payload.skill_id)
    let skillitem = skill.skillItems.find((skill_item) => skill_item.id == payload.id)
    this.questions.forEach((question) => {
      const questionSkill = question.skills[skill.name]
      if (!questionSkill) return

      const skillItemIndex = questionSkill.findIndex(item => item.name == skillitem.name)

      if (skillItemIndex < 0) return

      questionSkill.splice(skillItemIndex, 1, { name: payload.name, criteria: payload.criteria, answerItemsAttributes: [], promptAttributes: [] })
      question.updateBody()
      needReloadBody = true
    })
    skillitem.name = payload.name
    if (needReloadBody) this.body = questionJoiner(this.questions)
  }

  commitRemoveSkillItem(payload: { id: number, skill: { id: number } }) {
    let needReloadBody = false
    let skill = this.skills.find((skill) => skill.id == payload.skill.id);
    let skillitem = skill.skillItems.find((skill_item) => skill_item.id == payload.id);
    const skill_item_index = skill.skillItems.indexOf(skillitem);
    skill.skillItems.splice(skill_item_index, 1);
    this.questions.forEach((question) => {
      question.removeSkillItem(skill.name, skillitem.name)
    })
    this.body = questionJoiner(this.questions)
  }

  changePage(payload: { page: number }) {
    let targetPage = payload.page - 1;
    targetPage = Math.min(targetPage, this.questions.length - 1);
    targetPage = Math.max(targetPage, 0);

    this.currentPage = targetPage;
  }

  setSaveStatus(payload: { saveMessage: string, hasError: boolean }) {
    this.saveMessage = payload.saveMessage;
    this.hasError = payload.hasError;
  }

  commitSetCurrentLine(payload: { content: string, previousLineContent: string }) {
    this.currentLine = payload.content;
    this.previousLine = payload.previousLineContent;
  }

  openSidebarMenu(playload: { menu: string }) {
    this.showSidebarMenu = playload.menu == this.showSidebarMenu ? null : playload.menu
  }

  editBody(payload: { body: string }) {
    this.body = payload.body

    clearTimeout(setQuestionTimeout)
    setQuestionTimeout = setTimeout(() => {
      this.commitUpdateQuestions()
    })
  }

  async addSkill(payload) {
    const data = await SkillAPI.create(this.exam, payload.params)
    this.skills.unshift(new Skill(data.endpoints, data.id, data.name, []))
  }

  async updateSkill(payload) {
    const data = await SkillAPI.update(payload.skill, payload.params)
    this.commitUpdateSkill(data)
  }

  async removeSkill(payload) {
    SkillAPI.destroy(payload.skill)
    this.commitRemoveSkill(payload.skill)
  }

  async addSkillItem(payload) {
    const data = await SkillItemAPI.create(payload.skill, payload.params)
    this.commitAddSkillItem(data)
  }

  async updateSkillItem(payload) {
    const data = await SkillItemAPI.update(payload.skillItem, payload.params)
    this.commitUpdateSkillItem(data)
  }

  async removeSkillItem(payload) {
    SkillItemAPI.destroy(payload.skillItem)
    this.commitRemoveSkillItem(payload.skillItem)
  }

  insert(payload) {
    this.commitInsert(payload)
    this.commitUpdateQuestions()
  }

  wrap( payload) {
    this.commitWrap(payload)
    this.commitUpdateQuestions()
  }

  setFollowExamGenerationChanel(){
    if (!this.examGenerationChanelFollowed) {
      this.examGenerationChanelFollowed = true
      if (!this.examGenerationChanel) {
        this.examGenerationChanel = makeChannel('ChatExamGenerationChannel',
          async (e) =>  {
            if(e.data == "Done") {
              console.log("Done")
            } else {
              let chunk = (e.chunk as string || '')
              this.examGenerationChanelCallback(e.content, chunk, e.finished)
          }
        })
      }
      this.examGenerationChanel.send({
        action: 'follow',
        data: {
          exam_id: this.exam.id
        }
      })
    }
  }

  setStopExamGenerationChanel(){
    this.examGenerationChanel.send({
      action: 'stop',
      data: {
        exam_id: this.exam.id
      }
    })
  }

  setExamGenerationChanelCallback(callback: ExamGenerationChanelCallbackType){
    this.examGenerationChanelCallback = callback
  }

  async submitSave() {
    this.isLoading = true
    const forms = this.comparison.questionComparisons.filter(item => !item.isSame).map(item => item.form)
    try {
      const examData = await api.admin.exam.patch(this.exam.id, forms)
      this.commitResetData(humps.decamelizeKeys(examData.data))
      this.comparison = null
      this.setSaveStatus({ hasError: false, saveMessage: "保存しました" })
      this.isLoading = false
    } catch (error) {
      if (error instanceof ApiError) {
        this.isLoading = false
        this.setSaveStatus({ hasError: true, saveMessage: error.messagesString })
      } else {
        this.isLoading = false
        this.setSaveStatus({ hasError: true, saveMessage: error as any })
      }
    }

    this.setCreateRevision()
  }

  async saveQuestions() {
    const comparison = new Comparison(this.qs, this.questions)
    this.comparison = comparison
    return
  }

  async loadData() {
    const payload = await QuestionEditorAPI.loadDataAsyn()
    this.commitLoadData(payload)
    this.commitUpdateQuestions()
  }

  setEditorMode({editorMode}: {editorMode: EDITOR_MODE}){
    this.editorMode = editorMode

    if(editorMode === EDITOR_MODE.REVISION){
      this.latestBody = this.body
      this.getExamRevisions()
    }

    if (editorMode === EDITOR_MODE.EDITOR) {
      this.body = this.latestBody
      this.currentExamRevision = null
    }
  }

  async setCreateRevision(){
    const examRevisionForm: ExamRevisionForm = new ExamRevisionForm()
    examRevisionForm.body = this.body
    await api.admin.examRevision.post(this.exam.id, examRevisionForm)
    this.getExamRevisions()
  }

  async setCurrentExamRevision({examRevision}:{examRevision: ExamRevision | null}){
    this.currentExamRevision = examRevision

    if (examRevision) {
      this.body = examRevision.body
    }
  }

  async getExamRevisions(){
    const examRevisions = await api.admin.examRevision.get(this.exam.id)
    this.examRevisions = examRevisions
  }

  setExamRevision(){
    this.latestBody = this.currentExamRevision.body
    this.editorMode = EDITOR_MODE.EDITOR

    this.editBody({ body: this.currentExamRevision.body})
  }

  get cursoPosition(): CursoPosition {
    return {row: this.row, column: this.column}
  }

  get displayCurrentPage(): number {
    return this.currentPage + 1
  }

  get currentSelectedSkillItem(): (skillName: string, skillItemName: string) => boolean {
    return (skillName: string, skillItemName: string) => {
      const questionSkill = this.questions[this.currentPage].skills[skillName]
      if (!questionSkill) return false
      return questionSkill.findIndex(item => item.name == skillItemName) >= 0
    }
  }

  get question(): Question | undefined {
    return this.questions[this.currentPage]
  }

  get questionBody(): string {
    if (!this.questions[this.currentPage]) return ""
    const currentQuestion = this.questions[this.currentPage]
    const questions = this.questions.filter((question) => currentQuestion.group == question.group)
    return questions.map((question) => question.body).join("\n")
  }

  get isEditorMode() : boolean {
    return this.editorMode == EDITOR_MODE.EDITOR
  }

  get isRevisionMode() : boolean {
    return this.editorMode == EDITOR_MODE.REVISION
  }
}

function replaceAll(str: string, replaceStr: string, replaceWith: string) {
  let processStr = `${str}`
  while (true) {
    const newStr = processStr.replace(replaceStr, replaceWith)
    if (newStr === processStr) {
      return newStr
    }

    processStr = newStr
  }
  
}

export const qEditorState = new QEditorState()